import { useState, useEffect, useCallback, useRef } from "react";
import { showConfirm, showAlert, showPrompt } from "../lib/dialog";
import { AhrefsPanel, ClaudePanel, DataForSeoPanel, BrightDataPanel } from "./SettingsModal";
import { WpHelperContent } from "./SchemaSetupDialog";
import { getAhrefsKey, getAnthropicKey, getDfsCreds } from "../lib/apiKeys";
import { changePassword, listSites, addSite, getSite, updateSiteCredentials, removeSite, setHideWpTags, listSeats, createSeat, updateSeat, deleteSeat, generatePassword, type AuthUser, type SiteSummary, type Seat } from "../lib/authClient";
import { removeAuditServer, siteKey, getActiveSiteKey, clearAuditLocal } from "../lib/siteData";
import { describeConnection } from "../lib/fixExecutor";
import type { ConnectionInfo } from "../types";
import { buildBackup, parseBackup, restoreBackup, downloadBackup, backupNeedsPassphrase } from "../lib/backup";
import { GlobalInstructionsDialog } from "./GlobalInstructionsDialog";
import { GscConnectDialog } from "./GscConnectDialog";
import { GaConnectDialog } from "./GaConnectDialog";
import { GbpConnectDialog } from "./GbpConnectDialog";

// Settings panel opened from the sidebar (and the top-right gear). Two-pane,
// light-mode layout (category list on the left, content on the right) so new
// settings categories can be added easily.

type Category = "profile" | "savedsites" | "apis" | "wpintegration" | "data";

const CATEGORIES: { id: Category; label: string }[] = [
  { id: "profile",       label: "Profile"        },
  { id: "savedsites",    label: "Saved Websites" },
  { id: "apis",          label: "API Keys"       },
  { id: "wpintegration", label: "WP Integration" },
  { id: "data",          label: "Data"           },
  // ↑ Add future settings categories here.
];

// Monoline rail icons (one per category) — a cleaner, on-brand alternative to emoji markers.
const CAT_ICON: Record<Category, JSX.Element> = {
  profile:       <svg className="st-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="8" r="3.6"/><path d="M4.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6"/></svg>,
  savedsites:    <svg className="st-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17M12 3.5c2.4 2.3 3.7 5.4 3.7 8.5s-1.3 6.2-3.7 8.5c-2.4-2.3-3.7-5.4-3.7-8.5S9.6 5.8 12 3.5Z"/></svg>,
  apis:          <svg className="st-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="8" cy="15" r="4.2"/><path d="M11 12l8-8M16 7l2.5 2.5M13.5 9.5L16 12"/></svg>,
  wpintegration: <svg className="st-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M9 2.5v6M15 2.5v6M6.5 8.5h11v3a5.5 5.5 0 0 1-11 0Z"/><path d="M12 17v4.5"/></svg>,
  data:          <svg className="st-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><ellipse cx="12" cy="5.5" rx="7.5" ry="3"/><path d="M4.5 5.5v13c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3v-13M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3"/></svg>,
};

// One-line orientation under each tab's title.
const SUBTITLES: Record<Category, string> = {
  profile:       "Your account and package.",
  savedsites:    "Sites you audit and their connections.",
  apis:          "Connect the data providers the audit runs on.",
  wpintegration: "The Local SEO Garage plugin powers one-click fixes.",
  data:          "Reset generated content or back up everything.",
};

// localStorage keys holding generated / crawled tab content + crawl metadata.
// Cleared by "Reset All Tab Data". API keys, WP connection, competitor metrics,
// and cover details are intentionally NOT included.
const TAB_DATA_KEYS = [
  "full_audit_sections",
  "full_audit_analyses",
  "seo_audit_ever_run",
  "seo_tab_crawl_dates",
  "seo_last_tab_crawl",
  "cover_last_audit_date",
  "competitors_last_crawl",
];

export function SidebarSettings({ onClose, user, onLogout, onSitesChanged, onUserPatch, initialCategory, getClient }: { onClose: () => void; user?: AuthUser; onLogout: () => void; onSitesChanged?: () => void; onUserPatch?: (patch: Partial<AuthUser>) => void; initialCategory?: Category; getClient?: () => import("../lib/wpClient").WPClient | null }) {
  const [cat, setCat] = useState<Category>(initialCategory ?? "profile");
  const [resetting, setResetting] = useState(false);
  // WP Integration → agency toggle: hide the tool's "LSG …" marker tags on created pages.
  const [savingTags, setSavingTags] = useState(false);
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [credsOnlyImport, setCredsOnlyImport] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  function resetAllTabData() {
    void showConfirm(
      "This clears all generated/crawled tab content and crawl dates, returning every tab to its \"awaiting crawl\" state.\n\n" +
      "Your API keys, WordPress connection, and cover details are kept. Continue?",
      { title: "Reset All Tab Data", confirmLabel: "Clear & Reset", tone: "danger" },
    ).then(ok => {
      if (!ok) return;
      TAB_DATA_KEYS.forEach(k => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
      setResetting(true);
      setTimeout(() => window.location.reload(), 700);
    });
  }

  // Agency toggle: the checkbox is "Add LSG tracking tags…" so checked = tags on (hideWpTags=false).
  // Persist to the account, then patch the canonical user so the whole app (tag policy) picks it up.
  async function handleToggleTags(addTags: boolean) {
    if (savingTags) return;
    setSavingTags(true);
    setTagsError(null);
    try {
      const updated = await setHideWpTags(!addTags);
      onUserPatch?.({ hideWpTags: updated.hideWpTags });
    } catch (e) {
      setTagsError(e instanceof Error ? e.message : "Couldn't save. Try again.");
    } finally {
      setSavingTags(false);
    }
  }

  // ── Backup: export everything (sites' tab data + custom instructions, WP credentials, API
  // keys) to an optionally passphrase-encrypted file. ──────────────────────────────────────
  async function handleExport() {
    if (exporting) return;
    const pass = await showPrompt(
      "Enter a passphrase to encrypt this backup (recommended). You'll need it to restore. Leave blank to export WITHOUT encryption.",
      { title: "Export Backup", confirmLabel: "Export", placeholder: "Backup passphrase" },
    );
    if (pass === null) return; // cancelled
    if (!pass) {
      const ok = await showConfirm(
        "Exporting without a passphrase means the file will contain your WordPress application passwords and API keys in readable form. Anyone with the file could use them.\n\nExport unencrypted anyway?",
        { title: "Unencrypted Backup", confirmLabel: "Export Unencrypted", tone: "danger" },
      );
      if (!ok) return;
    }
    setExporting(true);
    try {
      const { file } = await buildBackup(pass || undefined);
      downloadBackup(file);
      await showAlert(pass ? "Backup exported (encrypted). Keep the file and passphrase safe." : "Backup exported. Store this file securely — it contains passwords in readable form.", { title: "Backup Exported" });
    } catch (e) {
      await showAlert(`Couldn't create the backup: ${e instanceof Error ? e.message : "error"}`, { title: "Export Failed", tone: "danger" });
    } finally {
      setExporting(false);
    }
  }

  // ── Backup: import a file. Full import overwrites each backed-up site's data + restores API
  // keys; the checkbox restores only WP credentials + per-site custom instructions. ─────────
  async function handleImportFile(file: File) {
    if (importing) return;
    setImporting(true);
    try {
      const text = await file.text();
      let passphrase: string | undefined;
      if (backupNeedsPassphrase(text)) {
        const p = await showPrompt("This backup is encrypted. Enter its passphrase to restore.", { title: "Import Backup", confirmLabel: "Decrypt", placeholder: "Backup passphrase" });
        if (p === null) return; // cancelled
        passphrase = p;
      }
      const payload = await parseBackup(text, passphrase);
      const ok = await showConfirm(
        credsOnlyImport
          ? `Restore WP credentials for ${payload.credentials.length} site(s), custom instructions for ${payload.audits.length} site(s), and your API keys? Your current tab data will NOT be changed.`
          : `Restore EVERYTHING from this backup? This will overwrite the tab data + custom instructions for ${payload.audits.length} site(s), restore ${payload.credentials.length} WP credential set(s), and replace your API keys. Sites not in the backup are left untouched. The app will reload afterward.`,
        { title: "Import Backup", confirmLabel: credsOnlyImport ? "Restore Creds & Instructions" : "Restore Everything", tone: credsOnlyImport ? "info" : "danger" },
      );
      if (!ok) return;
      const res = await restoreBackup(payload, { credsAndInstructionsOnly: credsOnlyImport });
      onSitesChanged?.();
      if (credsOnlyImport) {
        await showAlert(`Restored ${res.credentials} WP credential set(s), custom instructions${res.apiKeys ? ", and your API keys" : ""}. Current tab data was left unchanged.`, { title: "Import Complete" });
      } else {
        await showAlert(`Restored ${res.sites} site(s), ${res.credentials} credential set(s)${res.apiKeys ? ", and your API keys" : ""}. The app will now reload.`, { title: "Import Complete" });
        setTimeout(() => window.location.reload(), 400);
      }
    } catch (e) {
      await showAlert(`Import failed: ${e instanceof Error ? e.message : "error"}`, { title: "Import Failed", tone: "danger" });
    } finally {
      setImporting(false);
    }
  }

  const apiCount = (getAhrefsKey() ? 1 : 0) + (getAnthropicKey() ? 1 : 0) + (getDfsCreds().login ? 1 : 0);
  const [siteCount, setSiteCount] = useState<number | null>(null);
  const refreshSiteCount = useCallback(() => { listSites().then(s => setSiteCount(s.length)).catch(() => setSiteCount(null)); }, []);
  useEffect(() => { refreshSiteCount(); }, [refreshSiteCount]);
  const handleSitesChanged = () => { refreshSiteCount(); onSitesChanged?.(); };
  // Team seats: the owner can hide the API Keys and/or WP Integration tab for a seat account. Those
  // flags are set only on seats, so owners/normal accounts see all tabs.
  const visibleCategories = CATEGORIES.filter(c =>
    !(c.id === "apis" && user?.hideApiKeysTab) && !(c.id === "wpintegration" && user?.hideWpTab));
  // If the active tab was hidden (e.g. deep-linked), fall back to Profile.
  useEffect(() => {
    if (!visibleCategories.some(c => c.id === cat)) setCat("profile");
  }, [cat, visibleCategories]);
  const meta = CATEGORIES.find(c => c.id === cat);

  return (
    <div className="st-modal st-overlay" onClick={onClose}>
      <div className="st-win" onClick={e => e.stopPropagation()}>

        {/* Rail — matches the tool's sidebar per theme (navy dark / cream light) */}
        <nav className="st-rail" aria-label="Settings sections">
          <div className="st-brand">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3.2"/><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5 5l2.1 2.1M16.9 16.9L19 19M19 5l-2.1 2.1M7.1 16.9L5 19"/></svg>
            Settings
          </div>
          {visibleCategories.map(c => (
            <button key={c.id} className={`st-item${cat === c.id ? " on" : ""}`} onClick={() => setCat(c.id)}>
              {CAT_ICON[c.id]}
              <span className="st-label">{c.label}</span>
              {c.id === "apis" && <span className={`st-rchip${apiCount < 3 ? " partial" : ""}`}><span className="st-dot" />{apiCount}/3</span>}
              {c.id === "savedsites" && siteCount != null && siteCount > 0 && <span className="st-rchip">{siteCount}</span>}
            </button>
          ))}
          <div className="st-foot">
            <div className="who">Signed in as</div>
            <div className="email">{user?.email ?? ""}</div>
            <button className="logout" onClick={onLogout}>Log out</button>
          </div>
        </nav>

        {/* Content pane */}
        <div className="st-pane-wrap">
          <div className="st-head">
            <div>
              <h2>{meta?.label}</h2>
              {meta && SUBTITLES[meta.id] && <p className="sub">{SUBTITLES[meta.id]}</p>}
            </div>
            <button className="st-x" aria-label="Close settings" onClick={onClose}>✕</button>
          </div>

          <div className="st-body">
            <div className="st-pane on" key={cat}>
              {cat === "profile" && <ProfilePanel user={user} onLogout={onLogout} />}

              {cat === "savedsites" && <SavedWebsitesPanel onSitesChanged={handleSitesChanged} />}

              {cat === "apis" && !user?.hideApiKeysTab && (
                <>
                  <div className="st-sec">Ahrefs</div>
                  <AhrefsPanel />
                  <div className="st-sec spaced">Claude (Anthropic)</div>
                  <ClaudePanel />
                  <div className="st-sec spaced">DataForSEO</div>
                  <DataForSeoPanel />
                  <div className="st-sec spaced">Bright Data</div>
                  <BrightDataPanel />
                </>
              )}

              {cat === "wpintegration" && !user?.hideWpTab && (
                <>
                  <div className="st-sec" style={{ color: "var(--st-danger)" }}>Local SEO Garage (LSG) Plugin</div>
                  <WpHelperContent zipUrl="/local-seo-garage.zip" whiteLabel={!!user?.whiteLabel} brandName={user?.brandName ?? ""} />

                  {user?.whiteLabel && (
                    <div className="st-card">
                      <h3 className="st-wpb-h">Page tags on tool-created pages (Agency)</h3>
                      <p className="st-p">
                        When Local SEO Garage creates a page it adds the <code>LSG Tool</code> or <code>LSG Service Area Page</code> tags
                        dependent on the type of page created so you can spot Local SEO Garage-made pages in WordPress. Uncheck this to
                        not create those tags so your clients never see them on their sites. This applies to every website in your account.
                        Local SEO Garage still tracks historical data for new pages created or specific page updates made with those tools
                        either way — it assigns a key on each page into the database, so removing the tags changes nothing except what the
                        client sees.
                      </p>
                      <div className="st-toggle-row">
                        <button
                          type="button"
                          role="switch"
                          className="st-toggle"
                          aria-checked={!user?.hideWpTags}
                          disabled={savingTags}
                          onClick={() => void handleToggleTags(!!user?.hideWpTags)}
                        >
                          <span className="st-toggle-knob" />
                        </button>
                        <span>Add page tags in WordPress on pages created or modified by Local SEO Garage.</span>
                      </div>
                      {tagsError && <div className="st-note err">{tagsError}</div>}
                    </div>
                  )}

                  <div className="st-sec spaced">Other</div>
                  <MarkdownToggle getClient={getClient} />
                </>
              )}

              {cat === "data" && (
                <>
                  <div className="st-sec">Reset</div>
                  <p className="st-p">
                    Clear all generated/crawled tab content and crawl dates, returning every tab to its
                    &ldquo;awaiting crawl&rdquo; state. Your API keys, WordPress connection credentials for your websites,
                    global instructions for each website, and the business information details are kept.
                  </p>
                  <button className="st-btn danger" onClick={resetAllTabData} disabled={resetting}>
                    {resetting ? "Cleared — reloading…" : "Reset All Tab Data"}
                  </button>

                  <div className="st-sec spaced">Backup — Export</div>
                  <p className="st-p">
                    Download a single backup file with <strong>every saved website&rsquo;s tab data and custom instructions</strong>,
                    each site&rsquo;s <strong>WP connection credentials</strong>, and your <strong>API keys</strong>. You&rsquo;ll be asked
                    for a passphrase to encrypt it — keep the file and passphrase safe (the passphrase is required to restore and is never stored).
                  </p>
                  <button className="st-btn orange" onClick={() => void handleExport()} disabled={exporting}>
                    {exporting ? "Exporting…" : "⬇ Export Backup"}
                  </button>

                  <div className="st-sec spaced">Backup — Import</div>
                  <p className="st-p">
                    Restore from a backup file. A full import overwrites the tab data + custom instructions for each site in the
                    backup, restores WP credentials, and replaces your API keys (sites not in the backup are untouched); the app reloads after.
                  </p>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--st-ink2)", margin: "0 0 12px", cursor: "pointer" }}>
                    <input type="checkbox" checked={credsOnlyImport} onChange={e => setCredsOnlyImport(e.target.checked)} />
                    Only import WP credentials, custom instructions &amp; API keys (keep current tab data)
                  </label>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept="application/json,.json"
                    style={{ display: "none" }}
                    onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void handleImportFile(f); }}
                  />
                  <button className="st-btn" onClick={() => importInputRef.current?.click()} disabled={importing}>
                    {importing ? "Importing…" : "⬆ Import Backup"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Profile tab ───────────────────────────────────────────────
function ProfilePanel({ user, onLogout }: { user?: AuthUser; onLogout: () => void }) {
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [savingPw, setSavingPw] = useState(false);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (savingPw) return;
    setPwMsg(null);
    if (!curPw || !newPw) { setPwMsg({ ok: false, text: "Enter your current and new password." }); return; }
    if (newPw.length < 8) { setPwMsg({ ok: false, text: "New password must be at least 8 characters." }); return; }
    setSavingPw(true);
    try {
      await changePassword(curPw, newPw);
      setPwMsg({ ok: true, text: "Password updated." });
      setCurPw(""); setNewPw("");
    } catch (err) {
      setPwMsg({ ok: false, text: err instanceof Error ? err.message : "Could not change password." });
    } finally {
      setSavingPw(false);
    }
  }

  // A seat account (ownerEmail set) shows "Team Member Account" + the owning account's email instead
  // of the package tier, and never sees the seat-management section.
  const isSeat = !!user?.ownerEmail;
  return (
    <>
      <div className="st-sec">
        Account <span className="tier">({isSeat ? "Team Member Account" : user?.whiteLabel ? "Agency - White Label" : "Personal"})</span>
      </div>
      {isSeat && (
        <p className="st-p" style={{ marginTop: -4 }}>Seat of <strong>{user?.ownerEmail}</strong></p>
      )}
      <div className="st-form">
        <label>Name<input className="st-input" value={user?.name ?? ""} readOnly /></label>
        <label>Email<input className="st-input" value={user?.email ?? ""} readOnly /></label>
      </div>

      <div className="st-sec spaced">Change Password</div>
      <form className="st-form" onSubmit={submitPassword}>
        <input className="st-input" type="password" placeholder="Current password" autoComplete="current-password" value={curPw} onChange={e => setCurPw(e.target.value)} />
        <input className="st-input" type="password" placeholder="New password (min 8 chars)" autoComplete="new-password" value={newPw} onChange={e => setNewPw(e.target.value)} />
        {pwMsg && <div className={`st-note ${pwMsg.ok ? "ok" : "err"}`}>{pwMsg.text}</div>}
        <button className="st-btn good" type="submit" disabled={savingPw}>{savingPw ? "Saving…" : "Update Password"}</button>
      </form>

      {/* Agency owners manage their team seats here. Hidden for seats and non-agency accounts. */}
      {user?.whiteLabel && !isSeat && <SeatsSection />}

      <div className="st-sec spaced">Session</div>
      <button className="st-btn neutral" onClick={onLogout}>⎋ Log Out</button>
    </>
  );
}

// ── Additional Account Seats (Agency owner) ───────────────────
// Your Agency plan includes MAX_SEATS additional accounts tied to the main account. Each seat has an
// email + password, two "hide tab" checkboxes, and a delete button. A seat whose API Keys tab is
// hidden uses the owner's API keys for crawls.
const MAX_SEATS = 2;

function SeatsSection() {
  const [seats, setSeats] = useState<Seat[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    listSeats().then(setSeats).catch(e => { setErr(e instanceof Error ? e.message : "Could not load seats."); setSeats([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="st-sec spaced">Additional Account Seats</div>
      <p className="st-p">
        Your Agency plan includes <strong>{MAX_SEATS}</strong> additional seats. Give each teammate an email and
        password. They can sign in as their own account with their own crawled data. They can also update their
        password in their account. Hide the API Keys tab or hide the WP Integration tab to reduce access to API keys
        or the WordPress plugin download. If you hide the API Keys tab in Settings, your team accounts will still
        utilize the API keys in your account but will not have accesss to the API keys. If not hidden, they can see
        or add their own API keys. Deleting a seat removes that account and its crawl data and frees the slot.
      </p>
      {err && <div className="st-note err">{err}</div>}
      {seats == null ? (
        <p className="st-p">Loading…</p>
      ) : (
        <>
          {seats.map(s => <SeatCard key={s.id} seat={s} onChanged={load} />)}
          {seats.length < MAX_SEATS && <SeatCard key={`new-${seats.length}`} onChanged={load} />}
        </>
      )}
    </>
  );
}

// One seat row — editing an existing seat (seat set) or creating a new one (seat undefined).
function SeatCard({ seat, onChanged }: { seat?: Seat; onChanged: () => void }) {
  const isNew = !seat;
  const [email, setEmail] = useState(seat?.email ?? "");
  const [password, setPassword] = useState("");
  const [hideApiKeysTab, setHideApiKeysTab] = useState(!!seat?.hideApiKeysTab);
  const [hideWpTab, setHideWpTab] = useState(!!seat?.hideWpTab);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function save() {
    if (busy) return;
    setMsg(null);
    if (!email.trim()) { setMsg({ ok: false, text: "Enter an email." }); return; }
    if (isNew && password.length < 8) { setMsg({ ok: false, text: "Password must be at least 8 characters." }); return; }
    if (!isNew && password && password.length < 8) { setMsg({ ok: false, text: "Password must be at least 8 characters." }); return; }
    // Confirm before creating a live team account (skipped when just editing an existing seat).
    if (isNew && !(await showConfirm(
      `Create a team account for ${email.trim()}? They'll be able to sign in immediately with the password you set.`,
      { title: "Create team account", confirmLabel: "Create", tone: "create" },
    ))) return;
    setBusy(true);
    try {
      if (isNew) {
        const { invited } = await createSeat({ email: email.trim(), password, hideApiKeysTab, hideWpTab });
        setPassword("");
        onChanged();
        setMsg({ ok: true, text: invited
          ? `Seat created — an invite with the login was emailed to ${email.trim()}.`
          : "Seat created. Email isn't configured, so share the email and password with your teammate directly." });
        return;
      }
      await updateSeat(seat!.id, { email: email.trim(), hideApiKeysTab, hideWpTab, ...(password ? { password } : {}) });
      setPassword("");
      onChanged();
      setMsg({ ok: true, text: "Seat saved." });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : "Could not save the seat." });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy || isNew) return;
    if (!(await showConfirm(`Remove the seat ${seat!.email}? This frees the slot and deletes that account's data.`, { title: "Remove seat", confirmLabel: "Remove", tone: "danger" }))) return;
    setBusy(true);
    try { await deleteSeat(seat!.id); onChanged(); }
    catch (e) { setMsg({ ok: false, text: e instanceof Error ? e.message : "Could not remove the seat." }); setBusy(false); }
  }

  return (
    <div className="st-card">
      <div className="st-card-top">
        <span className="st-card-name">{isNew ? "New Seat" : "Seat"}</span>
        {!isNew && <button className="st-btn danger" onClick={() => void remove()} disabled={busy}>Delete</button>}
      </div>
      <div className="st-form" style={{ marginTop: 10 }}>
        <label>Email<input className="st-input" type="email" autoComplete="off" placeholder="teammate@example.com" value={email} onChange={e => setEmail(e.target.value)} /></label>
        <label>{isNew ? "Password" : "New password (blank = keep current)"}
          <div style={{ display: "flex", gap: 8 }}>
            <input className="st-input" type="text" autoComplete="new-password" placeholder={isNew ? "min 8 chars" : "leave blank to keep"} value={password} onChange={e => setPassword(e.target.value)} style={{ flex: 1 }} />
            <button type="button" className="st-btn ghost" onClick={() => setPassword(generatePassword())}>Generate</button>
          </div>
        </label>
      </div>
      <div className="st-toggle-row" style={{ marginTop: 10 }}>
        <button type="button" role="switch" className="st-toggle" aria-checked={hideApiKeysTab} onClick={() => setHideApiKeysTab(v => !v)}><span className="st-toggle-knob" /></button>
        <span>Hide the API Keys tab (this seat uses your API keys for crawls)</span>
      </div>
      <div className="st-toggle-row" style={{ marginTop: 8 }}>
        <button type="button" role="switch" className="st-toggle" aria-checked={hideWpTab} onClick={() => setHideWpTab(v => !v)}><span className="st-toggle-knob" /></button>
        <span>Hide the WP Integration tab</span>
      </div>
      {msg && <div className={`st-note ${msg.ok ? "ok" : "err"}`}>{msg.text}</div>}
      <button className="st-btn good" style={{ marginTop: 10 }} onClick={() => void save()} disabled={busy}>{busy ? "Saving…" : isNew ? "Create Seat" : "Save Seat"}</button>
    </div>
  );
}

// ── WP Integration → Other: per-site Markdown rendering toggle ─
// Reads/writes the connected site's lsg_markdown_enabled option via the plugin's /seo-audit/v1/markdown
// route (LSG plugin v1.24.4+). Degrades gracefully: no client → prompt to connect; older plugin →
// "not supported yet"; wp-config LSG_MARKDOWN=false → shown but locked off.
function MarkdownToggle({ getClient }: { getClient?: () => import("../lib/wpClient").WPClient | null }) {
  const client = getClient?.() ?? null;
  const [state, setState] = useState<{ supported: boolean; enabled: boolean; constantOff: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!client) { setState(null); return; }
    let alive = true;
    setLoading(true);
    client.getMarkdownEnabled()
      .then(s => { if (alive) setState(s); })
      .catch(() => { if (alive) setState({ supported: false, enabled: false, constantOff: false }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [client]);

  async function toggle() {
    if (!client || !state || saving || state.constantOff) return;
    setSaving(true); setErr(null);
    try {
      const r = await client.setMarkdownEnabled(!state.enabled);
      setState(s => (s ? { ...s, ...r } : s));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not update the setting.");
    } finally { setSaving(false); }
  }

  return (
    <div className="st-card">
      <h3 className="st-wpb-h">Markdown format</h3>
      <p className="st-p">
        Render pages written in Markdown as HTML on this website — a drop-in replacement for Jetpack&rsquo;s
        Markdown display. Only pages flagged as Markdown are affected, and while Jetpack&rsquo;s Markdown module
        is active it keeps handling them. Turn this off to stop the plugin from rendering Markdown on this site.
      </p>
      {!client ? (
        <p className="st-p" style={{ opacity: .8 }}>Connect a website (top of the page) to change this setting.</p>
      ) : loading || !state ? (
        <p className="st-p" style={{ opacity: .8 }}>Checking…</p>
      ) : !state.supported ? (
        <div className="st-note err">This site&rsquo;s plugin doesn&rsquo;t support the Markdown toggle yet. It updates automatically to v1.24.4+, then this control takes effect.</div>
      ) : (
        <>
          <div className="st-toggle-row">
            <button
              type="button"
              role="switch"
              className="st-toggle"
              aria-checked={state.enabled && !state.constantOff}
              disabled={saving || state.constantOff}
              onClick={() => void toggle()}
            >
              <span className="st-toggle-knob" />
            </button>
            <span>Enable Markdown format on this website.</span>
          </div>
          {state.constantOff && <div className="st-note err">Markdown is force-disabled on this site by the <code>LSG_MARKDOWN</code> constant in wp-config.php. Remove it to use this toggle.</div>}
          {err && <div className="st-note err">{err}</div>}
        </>
      )}
    </div>
  );
}

// ── Saved Websites tab ────────────────────────────────────────
function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Add OR edit a saved website's WordPress login. In edit mode (editSite set) the fields prefill
// from the site's stored credentials and Save updates it IN PLACE (keeping its id + GSC/GA/GBP
// connections); otherwise it adds a new site to Site Storage.
function SiteCredentialsDialog({ open, onClose, onSaved, editSite }: { open: boolean; onClose: () => void; onSaved: () => void; editSite?: SiteSummary | null }) {
  const isEdit = !!editSite;
  const [siteName, setSiteName] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [username, setUsername] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<ConnectionInfo | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // (Re)opening: clear for Add; for Edit, prefill from the site's stored credentials.
  useEffect(() => {
    if (!open) return;
    setTesting(false); setResult(null); setSaving(false);
    if (editSite) {
      setSiteName(editSite.label); setSiteUrl(editSite.siteUrl); setUsername(editSite.username); setAppPassword("");
      setLoading(true);
      getSite(editSite.id)
        .then(s => { setSiteName(s.label || editSite.label); setSiteUrl(s.siteUrl); setUsername(s.username); setAppPassword(s.appPassword); })
        .catch(() => { /* keep the summary values; the user can retype the password */ })
        .finally(() => setLoading(false));
    } else {
      setSiteName(""); setSiteUrl(""); setUsername(""); setAppPassword(""); setLoading(false);
    }
  }, [open, editSite]);

  if (!open) return null;

  const hasAll = !!(siteUrl.trim() && username.trim() && appPassword.trim());

  async function testConnection() {
    if (!hasAll) return;
    setTesting(true);
    setResult(null);
    try {
      setResult(await describeConnection(siteUrl.trim(), username.trim(), appPassword.trim()));
    } catch (e) {
      setResult({ ok: false, error: e instanceof Error ? e.message : "Connection failed" });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    if (!hasAll) return;
    setSaving(true);
    try {
      // Prefer the canonical URL resolved by a successful test; else the URL as typed.
      const urlToSave = result?.ok && result.siteUrl ? result.siteUrl : siteUrl.trim();
      if (editSite) {
        // Update in place — keeps the site's id and its GSC/GA/GBP connections + global instructions.
        await updateSiteCredentials(editSite.id, { siteUrl: urlToSave, username: username.trim(), appPassword: appPassword.trim(), label: siteName.trim() });
        onSaved();
        onClose();
        return;
      }
      const host = siteKey(urlToSave);
      let existing: SiteSummary | undefined;
      try { existing = (await listSites()).find(s => siteKey(s.siteUrl) === host); } catch { /* fall through to save */ }
      if (existing) {
        const update = await showConfirm(
          `"${existing.label}" (${existing.username}) is already saved for this website. Update its saved credentials with the Username and Application Password you just entered?`,
          { title: "Already saved", confirmLabel: "Update credentials", cancelLabel: "Cancel", tone: "info" },
        );
        if (!update) { setSaving(false); return; }
        try { await removeSite(existing.id); } catch { /* ignore */ }
      }
      await addSite({ siteUrl: urlToSave, username: username.trim(), appPassword: appPassword.trim(), label: siteName.trim() || undefined });
      onSaved();
      onClose();
    } catch (e) {
      void showAlert(e instanceof Error ? e.message : "Could not save the website.", { title: isEdit ? "Edit website" : "Add website", tone: "danger" });
    } finally {
      setSaving(false);
    }
  }

  const label: React.CSSProperties = { display: "block", fontSize: 10, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: "#64748b", marginBottom: 4 };
  const input: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: "9px 11px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontFamily: "'Roboto Mono',ui-monospace,monospace", color: "#0f172a", outline: "none" };
  const btn: React.CSSProperties = { padding: "8px 18px", border: "none", borderRadius: 0, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" };

  const fields = [
    { key: "url" as const, lbl: "Website URL", val: siteUrl, set: setSiteUrl, ph: "https://yoursite.com/", type: "text" },
    { key: "user" as const, lbl: "Username", val: username, set: setUsername, ph: "wp-username", type: "text" },
    { key: "pass" as const, lbl: "Application Password", val: appPassword, set: setAppPassword, ph: "xxxx xxxx xxxx xxxx xxxx xxxx", type: "password" },
  ];

  return (
    <div
      role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 4200, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "8vh 16px" }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 12, maxWidth: 480, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,.35)", padding: "22px 24px", color: "#334155", lineHeight: 1.5 }}
      >
        <h2 style={{ margin: "0 0 4px", fontSize: 17, color: "#0f172a", fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.7px" }}>
          {isEdit ? "Edit Website" : "Add Website"}
        </h2>
        <p style={{ fontSize: 12.5, color: "#475569", margin: "0 0 16px", lineHeight: 1.55 }}>
          {isEdit
            ? <>Update this website&rsquo;s WordPress login (an <strong>Application Password</strong>, not the account password). Test the connection, then save your changes.</>
            : <>Enter the website&rsquo;s WordPress login (an <strong>Application Password</strong>, not the account password). Test the connection, then save it to Site Storage.</>}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={label}>Website Name</label>
            <input
              type="text"
              value={siteName}
              onChange={e => setSiteName(e.target.value)}
              disabled={saving || loading}
              placeholder={loading ? "Loading…" : "A name for this website (optional)"}
              style={{ ...input, fontFamily: "'Inter','Mulish',sans-serif" }}
              autoComplete="off"
            />
          </div>
          {fields.map(f => (
            <div key={f.key}>
              <label style={label}>{f.lbl}</label>
              <input
                type={f.type}
                value={f.val}
                onChange={e => { f.set(e.target.value); setResult(null); }}
                disabled={saving || loading}
                placeholder={loading ? "Loading…" : f.ph}
                style={input}
                autoComplete={f.key === "pass" ? "new-password" : "off"}
              />
            </div>
          ))}
        </div>

        {/* Test Connection + result */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14, minHeight: 30 }}>
          <button
            onClick={testConnection}
            disabled={!hasAll || testing || saving || loading}
            style={{ ...btn, borderRadius: 6, background: "#e2e8f0", color: "#334155", opacity: !hasAll || testing || saving || loading ? 0.6 : 1, cursor: !hasAll || testing || saving || loading ? "not-allowed" : "pointer" }}
          >
            {testing ? "Testing…" : "Test Connection"}
          </button>
          {result && (
            <span style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.35, color: result.ok ? "#16a34a" : "#dc2626" }}>
              {result.ok
                ? "Connected"
                : `✗ ${result.error || "Connection failed"}`}
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button style={{ ...btn, background: "#e2e8f0", color: "#334155" }} onClick={onClose} disabled={saving}>Cancel</button>
          <button
            style={{ ...btn, background: "#16a34a", color: "#fff", opacity: !hasAll || saving || loading ? 0.6 : 1, cursor: !hasAll || saving || loading ? "not-allowed" : "pointer" }}
            onClick={handleSave}
            disabled={!hasAll || saving || loading}
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Save Website"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SavedWebsitesPanel({ onSitesChanged }: { onSitesChanged?: () => void }) {
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [busyId, setBusyId] = useState("");
  const [giSite, setGiSite] = useState<{ siteKey: string; label: string } | null>(null);
  const [gscSite, setGscSite] = useState<SiteSummary | null>(null);
  const [gaSite, setGaSite] = useState<SiteSummary | null>(null);
  const [gbpSite, setGbpSite] = useState<SiteSummary | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editSite, setEditSite] = useState<SiteSummary | null>(null);

  const refresh = useCallback(() => { listSites().then(setSites).catch(() => setSites([])); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Newest first, so a just-added website appears at the top of the list.
  const orderedSites = [...sites].sort((a, b) => (b.addedAt || "").localeCompare(a.addedAt || ""));

  function deleteSite(site: SiteSummary) {
    void showConfirm(
      `Delete "${site.label}" (${site.username})?\n\nThis permanently removes the saved website AND all of its reports. This cannot be undone.`,
      { title: "Delete website", confirmLabel: "Delete website & reports", cancelLabel: "Cancel", tone: "danger" },
    ).then(async ok => {
      if (!ok) return;
      const key = siteKey(site.siteUrl);
      setBusyId(site.id);
      try {
        await removeSite(site.id);          // saved WP credentials
        await removeAuditServer(key);       // its report bucket
      } catch { /* ignore */ }
      // If we just deleted the site currently loaded in the app, clear it locally and reload
      // so the app re-resolves a valid active site.
      if (key && key === getActiveSiteKey()) {
        clearAuditLocal();
        window.location.reload();
        return;
      }
      setBusyId("");
      refresh();
      onSitesChanged?.(); // refresh the Site Storage dropdown in the cover bar
    });
  }

  return (
    <>
      <p className="st-p">
        Websites you've saved (with their WordPress login). Deleting one removes its saved
        WordPress connection credentials, its audit reports in each tab, and its global instructions.
      </p>

      <button type="button" className="st-add-site" onClick={() => setAddOpen(true)}>
        <span className="st-add-site-plus" aria-hidden="true">+</span>
        <span className="st-add-site-text">
          <span className="st-add-site-title">Add Website</span>
          <span className="st-add-site-sub">Enter WordPress credentials and test the connection</span>
        </span>
      </button>

      {sites.length === 0 ? (
        <p className="st-p" style={{ color: "var(--st-ink3)" }}>
          No saved websites yet. Add one above, or save one from the <strong>Site Storage</strong> dropdown above the WP Connection panel.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {orderedSites.map(s => (
            <div key={s.id} className="st-card">
              <div className="st-card-top">
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <div className="st-card-name" style={{ textTransform: "none", fontFamily: "var(--st-mono)", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: "0 1 auto", minWidth: 0 }}>{s.label}</div>
                    <button className="st-edit" onClick={() => setEditSite(s)} title="Edit login credentials" aria-label="Edit login credentials">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                    </button>
                  </div>
                  <div className="st-site-meta">Added {fmtDate(s.addedAt)}</div>
                </div>
                <button className="st-trash" onClick={() => deleteSite(s)} disabled={busyId === s.id} title="Delete website and its reports" aria-label="Delete website" style={{ opacity: busyId === s.id ? 0.5 : 1 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16"/><path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7"/><path d="M6.5 7l.85 12.1A1.5 1.5 0 0 0 8.85 20.5h6.3a1.5 1.5 0 0 0 1.5-1.4L17.5 7"/><path d="M10 11v5.5M14 11v5.5"/></svg>
                </button>
              </div>
              <div className="st-conn-row">
                <button className="st-conn gi" onClick={() => setGiSite({ siteKey: siteKey(s.siteUrl), label: s.label })} title="Edit site-wide instructions prefilled into every new page's Custom Instructions">⚙ Global Instructions</button>
                <button className={`st-conn ${s.gscConnected ? "on" : "off"}`} onClick={() => setGscSite(s)} title="Connect this website to Google Search Console and pick its property">{s.gscConnected ? "✓ GSC Connected" : "Google Search Console"}</button>
                <button className={`st-conn ${s.gaConnected ? "on" : "off"}`} onClick={() => setGaSite(s)} title="Connect this website to Google Analytics (GA4) and pick its property">{s.gaConnected ? "✓ GA Connected" : "Google Analytics"}</button>
                <button className={`st-conn ${s.gbpConnected ? "on" : "off"}`} onClick={() => setGbpSite(s)} title="Search & select the Google Business Profile for this website (shown in the GBP Review Manager tab)">{s.gbpConnected ? "✓ GBP Connected" : "GBP Profile"}</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <SiteCredentialsDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={() => { refresh(); onSitesChanged?.(); }}
      />
      <SiteCredentialsDialog
        open={!!editSite}
        editSite={editSite}
        onClose={() => setEditSite(null)}
        onSaved={() => { refresh(); onSitesChanged?.(); }}
      />
      <GlobalInstructionsDialog
        open={!!giSite}
        siteKey={giSite?.siteKey ?? ""}
        label={giSite?.label ?? ""}
        onClose={() => setGiSite(null)}
      />
      <GscConnectDialog
        open={!!gscSite}
        site={gscSite}
        onClose={() => setGscSite(null)}
        onChanged={refresh}
      />
      <GaConnectDialog
        open={!!gaSite}
        site={gaSite}
        onClose={() => setGaSite(null)}
        onChanged={refresh}
      />
      <GbpConnectDialog
        open={!!gbpSite}
        site={gbpSite}
        onClose={() => setGbpSite(null)}
        onChanged={() => { refresh(); onSitesChanged?.(); }}
      />
    </>
  );
}
